/**
 * useGame — central game state hook.
 *
 * Manages session lifecycle, word expansion, word combination,
 * graph updates, scoring, and victory detection.
 *
 * Node state is mirrored to a ref (`nodesRef`) so async callbacks
 * always read the latest graph without stale closures.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { gameAPI } from '../services/api';
import { processExpansionResults } from '../utils/graph';

const VICTORY_DELAY = 800;

export default function useGame() {
  // Session
  const [sessionId, setSessionId] = useState(null);
  const [targetWord, setTargetWord] = useState(null);
  const [initialWord, setInitialWord] = useState(null);

  // Progress
  const [expansions, setExpansions] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);

  // Scoring
  const [potentialScore, setPotentialScore] = useState(0);
  const [scoreData, setScoreData] = useState(null);
  const [difficultyMultiplier, setDifficultyMultiplier] = useState(1.0);
  const [difficultyPercentile, setDifficultyPercentile] = useState(50);

  // Distance tracking — powers the warm/cool indicator
  const [initialSimilarity, setInitialSimilarity] = useState(0);
  const [closestSimilarity, setClosestSimilarity] = useState(0);

  // Graph
  const [nodes, setNodes] = useState([]);
  const [links, setLinks] = useState([]);
  const [selectedWord, setSelectedWord] = useState(null);
  const [lastExpandedWord, setLastExpandedWord] = useState(null);
  const [expansionFocusIds, setExpansionFocusIds] = useState(null);
  const [lastCombineResult, setLastCombineResult] = useState(null);

  // Victory animation
  const [victoryPending, setVictoryPending] = useState(false);
  const [winningWord, setWinningWord] = useState(null);

  // UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs
  const expandedWords = useRef(new Set());
  const expandingWords = useRef(new Set());
  const combiningRef = useRef(false);
  const victoryTimeout = useRef(null);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  useEffect(() => {
    return () => {
      if (victoryTimeout.current) clearTimeout(victoryTimeout.current);
    };
  }, []);

  // -----------------------------------------------------------------
  // Shared: handle victory from expand or combine responses
  // -----------------------------------------------------------------

  const handleVictory = useCallback((data) => {
    setScoreData({
      score: data.score,
      baseScore: data.baseScore,
      quickWinBonus: data.quickWinBonus,
      difficultyMultiplier: data.difficultyMultiplier,
      difficultyBonus: data.difficultyBonus,
      rating: data.rating,
    });
    setWinningWord(targetWord?.word);
    setVictoryPending(true);

    victoryTimeout.current = setTimeout(() => {
      setGameComplete(true);
      setVictoryPending(false);
    }, VICTORY_DELAY);
  }, [targetWord]);

  // -----------------------------------------------------------------
  // Start new game
  // -----------------------------------------------------------------

  const startNewGame = useCallback(async (difficulty = 50) => {
    setLoading(true);
    setError(null);

    try {
      const data = await gameAPI.startGame(difficulty);

      setSessionId(data.sessionId);
      setTargetWord(data.targetWord);
      setInitialWord(data.initialWord);
      setSelectedWord(data.initialWord.word);
      setLastExpandedWord(null);
      setExpansionFocusIds(null);
      setLastCombineResult(null);
      setExpansions(0);
      setGameComplete(false);
      setVictoryPending(false);
      setWinningWord(null);
      setScoreData(null);
      setPotentialScore(data.maxScore);
      setDifficultyMultiplier(data.difficultyMultiplier || 1.0);
      setDifficultyPercentile(data.difficultyPercentile || 50);
      setInitialSimilarity(data.targetSimilarity || 0);
      setClosestSimilarity(data.targetSimilarity || 0);

      expandedWords.current = new Set();
      expandingWords.current = new Set();
      combiningRef.current = false;
      if (victoryTimeout.current) {
        clearTimeout(victoryTimeout.current);
        victoryTimeout.current = null;
      }

      setNodes([{
        id: data.initialWord.word,
        word: data.initialWord.word,
        emoji: data.initialWord.emoji || '',
        isExpanded: false,
      }]);
      setLinks([]);
    } catch (err) {
      setError(err.message || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  }, []);

  // -----------------------------------------------------------------
  // Expand a word node
  // -----------------------------------------------------------------

  const expandWord = useCallback(async (word) => {
    if (gameComplete || victoryPending) return;
    if (expandingWords.current.has(word)) return;
    if (loading) return;
    if (!word || typeof word !== 'string') return;

    const isReexpansion = expandedWords.current.has(word);
    expandingWords.current.add(word);
    setLoading(true);
    setError(null);

    try {
      const existingIds = new Set(nodesRef.current.map(n => n.id));
      const data = await gameAPI.expandWord(sessionId, word);

      expandedWords.current.add(word);
      setExpansions(data.expansions);
      setPotentialScore(data.potentialScore);
      setLastExpandedWord(word);
      setLastCombineResult(null);

      // Track closest approach to target
      const maxSim = Math.max(
        ...data.similarWords.map(sw => sw.similarityToTarget || 0),
      );
      setClosestSimilarity(prev => Math.max(prev, maxSim));

      const { newNodes, newLinks } = processExpansionResults(
        word,
        data.similarWords,
        data.crossLinks,
        existingIds,
        targetWord?.word,
        data.isTargetReached,
        isReexpansion,
      );

      const focusIds = new Set([word, ...newNodes.map(n => n.id)]);
      setExpansionFocusIds(focusIds);

      setNodes(prev => {
        const updated = prev.map(n =>
          n.id === word ? { ...n, isExpanded: true } : n,
        );
        return [...updated, ...newNodes];
      });
      setLinks(prev => [...prev, ...newLinks]);

      if (data.isTargetReached) handleVictory(data);
    } catch (err) {
      setError(err.message || 'Failed to expand word');
    } finally {
      setLoading(false);
      expandingWords.current.delete(word);
    }
  }, [sessionId, loading, gameComplete, victoryPending, targetWord, handleVictory]);

  // -----------------------------------------------------------------
  // Combine two words
  // -----------------------------------------------------------------

  const combineWords = useCallback(async (word1, word2) => {
    if (gameComplete || victoryPending) return;
    if (combiningRef.current || loading) return;
    if (!word1 || !word2 || word1 === word2) return;

    combiningRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const existingWords = nodesRef.current.map(n => n.id);
      const data = await gameAPI.combineWords(sessionId, word1, word2, existingWords);

      setExpansions(data.expansions);
      setPotentialScore(data.potentialScore);

      // Track closest approach to target
      const combinedResults = data.combinedWords; // array of {word, similarityToWord1, similarityToWord2}
      const maxSim = Math.max(
        ...combinedResults.map(r => r.similarityToTarget || 0),
      );
      setClosestSimilarity(prev => Math.max(prev, maxSim));
      const combinedWordNames = combinedResults.map(r => r.word);

      // Position new nodes in a fan perpendicular to the line between parents
      const n1 = nodesRef.current.find(n => n.id === word1);
      const n2 = nodesRef.current.find(n => n.id === word2);

      const newNodes = [];
      const newLinks = [];

      for (let i = 0; i < combinedResults.length; i++) {
        const r = combinedResults[i];
        let initX, initY;
        if (n1?.x !== undefined && n2?.x !== undefined) {
          const mx = (n1.x + n2.x) / 2;
          const my = (n1.y + n2.y) / 2;
          const dx = n2.x - n1.x;
          const dy = n2.y - n1.y;
          const len = Math.sqrt(dx * dx + dy * dy) || 1;
          // Fan out: first result at center, others offset along the perpendicular
          const spread = combinedResults.length > 1
            ? (i - (combinedResults.length - 1) / 2) * 60
            : 0;
          const perpX = -dy / len;
          const perpY = dx / len;
          // Tangent direction (along the parent line) for fanning
          const tanX = dx / len;
          const tanY = dy / len;
          initX = mx + perpX * 80 + tanX * spread;
          initY = my + perpY * 80 + tanY * spread;
        }

        newNodes.push({
          id: r.word,
          word: r.word,
          emoji: r.emoji || '',
          isExpanded: false,
          isCombination: true,
          ...(initX !== undefined && { x: initX, y: initY }),
        });
        newLinks.push(
          { source: word1, target: r.word, similarity: r.similarityToWord1 },
          { source: word2, target: r.word, similarity: r.similarityToWord2 },
        );
      }

      // Cross-links between combined results
      for (const cl of (data.crossLinks || [])) {
        newLinks.push({
          source: cl.word1,
          target: cl.word2,
          similarity: cl.similarity,
        });
      }

      setExpansionFocusIds(new Set(combinedWordNames));
      setLastExpandedWord(null);
      setLastCombineResult({ parents: [word1, word2], results: combinedWordNames });
      setNodes(prev => [...prev, ...newNodes]);
      setLinks(prev => [...prev, ...newLinks]);

      if (data.isTargetReached) handleVictory(data);
    } catch (err) {
      setError(err.message || 'Failed to combine words');
    } finally {
      setLoading(false);
      combiningRef.current = false;
    }
  }, [sessionId, loading, gameComplete, victoryPending, handleVictory]);

  // -----------------------------------------------------------------
  // Node click
  // -----------------------------------------------------------------

  const handleNodeClick = useCallback((word) => {
    setSelectedWord(word);
    expandWord(word);
  }, [expandWord]);

  // No auto-start — SoloGame calls startNewGame() on mount

  // -----------------------------------------------------------------
  // Derived
  // -----------------------------------------------------------------

  // Warmth: 0 = cold (starting distance), 1 = found target
  // Normalized so the indicator shows meaningful progress
  const warmth = initialSimilarity < 1
    ? Math.max(0, Math.min(1,
        (closestSimilarity - initialSimilarity) / (1 - initialSimilarity)
      ))
    : 0;

  return {
    nodes, links, selectedWord, lastExpandedWord, expansionFocusIds, lastCombineResult,
    targetWord, initialWord,
    expansions, gameComplete,
    potentialScore, scoreData,
    difficultyMultiplier, difficultyPercentile,
    victoryPending, winningWord,
    loading, error,
    warmth, closestSimilarity, initialSimilarity,
    startNewGame, handleNodeClick, combineWords,
  };
}
